import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { X, Loader2, BellRing, Save, Sliders } from "lucide-react";

const SCORE_FIELDS = [
  ["min_overall_score", "Overall case score"],
  ["min_injury_score", "Injury score"],
  ["min_coverage_score", "Coverage score"],
  ["min_liability_score", "Liability score"],
];

const Toggle = ({ checked, onChange, label, hint, testid }) => (
  <label className="flex items-start justify-between gap-3 py-2 cursor-pointer">
    <span>
      <span className="text-sm font-medium text-navy">{label}</span>
      {hint && <span className="block text-xs text-slate-400">{hint}</span>}
    </span>
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
      className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${checked ? "bg-cobalt" : "bg-slate-300"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  </label>
);

export default function AlertPreferences({ onClose }) {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/attorney/alert-preferences").then((r) => setPrefs({ ...r.data, states: (r.data.states || []).join(", ") })).catch(() => onClose());
  }, [onClose]);

  const set = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));
  const toggleOrigin = (o) => setPrefs((p) => {
    const has = p.origins.includes(o);
    const origins = has ? p.origins.filter((x) => x !== o) : [...p.origins, o];
    return { ...p, origins: origins.length ? origins : p.origins };
  });

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...prefs, states: String(prefs.states || "").split(",").map((s) => s.trim()).filter(Boolean) };
      await api.put("/attorney/alert-preferences", payload);
      toast.success("Alert preferences saved");
      onClose(true);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="alert-prefs-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[88vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-heading font-extrabold text-navy flex items-center gap-2"><BellRing className="w-5 h-5 text-cobalt" /> Marketplace Alert Preferences</h3>
          <button onClick={() => onClose(false)} className="p-1.5 rounded-full hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        {!prefs ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>
        ) : (
          <>
            <div className="p-6 overflow-y-auto space-y-5">
              <div className="rounded-2xl bg-slate-50 px-4 py-1 divide-y divide-slate-100">
                <Toggle checked={prefs.marketplace_enabled} onChange={(v) => set("marketplace_enabled", v)}
                  label="Notify me about new marketplace cases" hint="Master switch for first-to-claim alerts" testid="pref-master" />
              </div>

              <div className={prefs.marketplace_enabled ? "space-y-5" : "space-y-5 opacity-40 pointer-events-none"}>
                {/* Score thresholds */}
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5 mb-2"><Sliders className="w-3.5 h-3.5" /> Minimum scores</div>
                  <div className="space-y-4">
                    {SCORE_FIELDS.map(([k, label]) => (
                      <div key={k}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-navy">{label}</span>
                          <span className="font-semibold text-cobalt">{prefs[k] === 0 ? "Any" : `${prefs[k]}+`}</span>
                        </div>
                        <input type="range" min={0} max={100} step={5} value={prefs[k]} onChange={(e) => set(k, Number(e.target.value))}
                          data-testid={`pref-${k}`} className="w-full accent-cobalt mt-1" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* States */}
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">States (comma-separated, blank = all)</label>
                  <input value={prefs.states} onChange={(e) => set("states", e.target.value)} placeholder="e.g. NC, SC, GA"
                    data-testid="pref-states" className="mt-1 w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm uppercase outline-none focus:border-cobalt" />
                </div>

                {/* Origins */}
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Case source</div>
                  <div className="flex gap-2">
                    {[["organic", "Organic"], ["affiliate", "Affiliate"]].map(([o, l]) => (
                      <button key={o} type="button" onClick={() => toggleOrigin(o)} data-testid={`pref-origin-${o}`}
                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${prefs.origins.includes(o) ? "bg-cobalt text-white border-cobalt" : "bg-white border-slate-200 text-slate-500"}`}>{l}</button>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl bg-slate-50 px-4 py-1 divide-y divide-slate-100">
                  <Toggle checked={prefs.commercial_only} onChange={(v) => set("commercial_only", v)}
                    label="Commercial vehicle cases only" testid="pref-commercial" />
                  <Toggle checked={prefs.sms_enabled} onChange={(v) => set("sms_enabled", v)}
                    label="Also send me a text message (SMS)" hint="Requires a phone number on your profile" testid="pref-sms" />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
              <button onClick={() => onClose(false)} className="px-5 py-2.5 rounded-full bg-slate-100 text-slate-600 text-sm font-medium">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="pref-save" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cobalt text-white text-sm font-semibold hover:bg-cobalt-hover disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save preferences</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
